import { useCallback, useEffect, useRef, useState } from "react";
import { collection, onSnapshot, query, Timestamp, where } from "firebase/firestore";
import { db } from "../firebase";

const NOTIF_SOUND = "/notification.mp3";
const NOTIFICATION_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;

export function useBookingWatcher(role = "admin") {
	const [permission, setPermission] = useState(
		typeof Notification !== "undefined" ? Notification.permission : "unsupported"
	);
	const [toast, setToast] = useState(null);
	const audioRef = useRef(null);

	useEffect(() => {
		audioRef.current = new Audio(NOTIF_SOUND);
	}, []);

	const requestPermission = useCallback(async () => {
		if (typeof Notification === "undefined") return;
		const result = await Notification.requestPermission();
		setPermission(result);
	}, []);

	const notify = useCallback((title, body, id, kind) => {
		if (permission === "granted") {
			const notification = new Notification(title, { body, icon: "/lg.png" });
			notification.onclick = () => {
				window.focus();
				notification.close();
			};
		}
		setToast({ title, body, id, kind });
		audioRef.current?.play().catch(() => {});
	}, [permission]);

	useEffect(() => {
		if (!role) return undefined;

		const allWatchers = [
			{
				collectionName: "bookings",
				timestampField: "bookedAt",
				getStatus: (data) => data.paymentStatus || "unpaid",
				isPaid: (status) => status === "paid",
				onAdded: (data, id) => notify(
					"New appointment booking",
					`${[data.name, data.surname].filter(Boolean).join(" ") || "A customer"} booked ${data.serviceTitle || data.service || "a session"}${data.date ? ` for ${data.date}` : ""}`,
					id,
					"booking"
				),
				onPaid: (data, id) => notify(
					"Appointment payment received",
					`${[data.name, data.surname].filter(Boolean).join(" ") || "A customer"} paid for ${data.serviceTitle || "an appointment"}`,
					id,
					"purchase"
				),
			},
			{
				collectionName: "bookOrders",
				timestampField: "createdAt",
				getStatus: (data) => data.status || "pending",
				isPaid: (status) => status === "paid",
				onAdded: (data, id) => {
					if (data.status === "paid") {
						notify(
							"New paid book order",
							`${data.buyerName || "A customer"} purchased ${data.bookTitle || "a book"} (${data.format === "hardCopy" ? "hard copy" : "PDF"})`,
							id,
							"purchase"
						);
					}
				},
				onPaid: (data, id) => notify(
					"New paid book order",
					`${data.buyerName || "A customer"} purchased ${data.bookTitle || "a book"} (${data.format === "hardCopy" ? "hard copy" : "PDF"})`,
					id,
					"purchase"
				),
			},
			{
				collectionName: "ticketOrders",
				timestampField: "createdAt",
				getStatus: (data) => data.status || "pending",
				isPaid: (status) => status === "paid" || status === "free",
				onAdded: (data, id) => {
					if (data.status === "paid" || data.status === "free") {
						notify(
							data.status === "free" ? "New free ticket booking" : "New paid ticket purchase",
							`${[data.buyerName, data.buyerSurname].filter(Boolean).join(" ") || "A customer"} booked ${data.quantity || 1} ticket${Number(data.quantity) === 1 ? "" : "s"} for ${data.eventTitle || "an event"}`,
							id,
							"purchase"
						);
					}
				},
				onPaid: (data, id) => notify(
					"New paid ticket purchase",
					`${[data.buyerName, data.buyerSurname].filter(Boolean).join(" ") || "A customer"} booked ${data.quantity || 1} ticket${Number(data.quantity) === 1 ? "" : "s"} for ${data.eventTitle || "an event"}`,
					id,
					"purchase"
				),
			},
		];
		const watchers = role === "team"
			? [{
				...allWatchers.find((watcher) => watcher.collectionName === "bookings"),
				getStatus: () => null,
				isPaid: () => false,
			}]
			: allWatchers;

		const unsubscribers = watchers.map((watcher) => {
			let initialSnapshot = true;
			const previousStatuses = new Map();
			const recentRecordsQuery = query(
				collection(db, watcher.collectionName),
				where(
					watcher.timestampField,
					">=",
					Timestamp.fromDate(new Date(Date.now() - NOTIFICATION_LOOKBACK_MS))
				)
			);
			return onSnapshot(
				recentRecordsQuery,
				(snapshot) => {
					if (initialSnapshot) {
						snapshot.docs.forEach((item) => {
							previousStatuses.set(item.id, watcher.getStatus(item.data()));
						});
						initialSnapshot = false;
						return;
					}

					snapshot.docChanges().forEach((change) => {
						const data = change.doc.data();
						const nextStatus = watcher.getStatus(data);
						const previousStatus = previousStatuses.get(change.doc.id);
						previousStatuses.set(change.doc.id, nextStatus);

						if (watcher.collectionName === "bookings" && change.type === "added") {
							watcher.onAdded(data, change.doc.id);
							return;
						}
						if (role === "team") return;

						if (change.type === "added" && watcher.isPaid(nextStatus)) {
							watcher.onAdded(data, change.doc.id);
							return;
						}

						if (
							change.type === "modified" &&
							watcher.isPaid(nextStatus) &&
							!watcher.isPaid(previousStatus)
						) {
							watcher.onPaid(data, change.doc.id);
						}
					});
				},
				(error) => console.error(`${watcher.collectionName} notification watcher failed:`, error)
			);
		});

		return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
	}, [role, notify]);

	const dismissToast = useCallback(() => setToast(null), []);

	return { permission, requestPermission, toast, dismissToast };
}
